import * as React from 'react';
import classnames from 'classnames';

import './style.scss';
import { FormattedMessage } from 'react-intl';
import { ObjectStatus, PremiseStatus, RentalStatus, StageStatus } from 'features/search/filters/enums';
import { DocumentStatus } from 'features/userDocuments/enums';
import { CaseStatus } from 'features/errands/enums';

export enum TagDesign {
    DEFAULT = 'default',
    DOT = 'dot',
    OLD = 'old',
}
export enum TagType {
    OBJECT = 'object',
    PROJECT = 'project',
    RENTAL = 'rental',
    PREMISE = 'premise',
    CASE = 'case',
    DOCUMENT = 'document',
}
export enum TagColor {
    DEFAULT = 'default',
    
    SENIOR = 'senior',
    PREMIUM = 'premium',
    SMARTA = 'smarta',
    
    BLACK = 'black',
    BLUE = 'blue',
    GRAY = 'gray',
    GREEN = 'green',
    ORANGE = 'orange',
    RED = 'red',
    YELLOW = 'yellow',
}

interface Props {
    tagDesign: TagDesign;
    tagColor?: TagColor;
    tagText?: string | any;
    status?: StageStatus | ObjectStatus | DocumentStatus | PremiseStatus | CaseStatus | RentalStatus;
    className?: string;
}

export const Tags: React.FunctionComponent<Props> = ({tagDesign, tagColor, tagText, status, className}) => {
    interface TagValues {
        textId: string;
        color: TagColor;
        type: TagType | undefined;
    }
    const sellingObjectTag: TagValues = {
        textId: 'jm.components.ResidenceTags.sellingObject',
        color: TagColor.GREEN,
        type: TagType.OBJECT,
    };
    const sellingProjectTag: TagValues = {
        textId: 'jm.components.ResidenceTags.selling',
        color: TagColor.GREEN,
        type: TagType.PROJECT,
    };

    const tagColorMap: Partial<Record<TagColor, TagValues>> = {
        [TagColor.SENIOR]: {
            textId: 'jm.components.ResidenceTags.senior',
            color: TagColor.SENIOR,
            type: undefined,
        },

        [TagColor.PREMIUM]: {
            textId: 'jm.components.ResidenceTags.premium',
            color: TagColor.PREMIUM,
            type: undefined,
        },

        [TagColor.SMARTA]: {
            textId: 'jm.components.ResidenceTags.smartHousing',
            color: TagColor.SMARTA,
            type: undefined,
        },
    };

    const statusMap: Record<string, TagValues> = {
        // OBJECTS
        [ObjectStatus.FOR_SALE]: sellingObjectTag,
        [ObjectStatus.SUCCESSION]: sellingObjectTag,
        [ObjectStatus.RESERVED]: {
            textId: 'jm.components.ResidenceTags.reserved',
            color: TagColor.ORANGE,
            type: TagType.OBJECT,
        },
        [ObjectStatus.BOOKED]: {
            textId: 'jm.components.ResidenceTags.booked',
            color: TagColor.GRAY,
            type: TagType.OBJECT,
        },
        [ObjectStatus.SOLD]: {
            textId: 'jm.components.ResidenceTags.soldoutObject',
            color: TagColor.RED,
            type: TagType.OBJECT,
        },

        // PROJECTS
        [StageStatus.PLANNED]: {
            textId: 'jm.components.ResidenceTags.comming',
            color: TagColor.BLUE,
            type: TagType.PROJECT,
        },
        [StageStatus.VIP]: {
            textId: 'jm.components.ResidenceTags.vipSale',
            color: TagColor.YELLOW,
            type: TagType.PROJECT,
        },
        [StageStatus.FORSALE]: sellingProjectTag,
        [StageStatus.MOVE_IN_READY]: sellingProjectTag,
        [StageStatus.SUCCESSION]: sellingProjectTag,
        [StageStatus.SOLD_OUT]: {
            textId: 'jm.components.ResidenceTags.soldout',
            color: TagColor.RED,
            type: TagType.PROJECT,
        },
        [StageStatus.REFERENCE]: {
            textId: 'jm.components.ResidenceTags.reference',
            color: TagColor.GRAY,
            type: TagType.PROJECT,
        },

        // DOCUMENTS
        [DocumentStatus.DONE]: {
            textId: 'jm.components.MyDocuments.DocumentsTable.statusDone',
            color: TagColor.GREEN,
            type: TagType.DOCUMENT,
        },
        [DocumentStatus.PENDING]: {
            textId: 'jm.components.MyDocuments.DocumentsTable.statusPending',
            color: TagColor.ORANGE,
            type: TagType.DOCUMENT,
        },

        // PREMISES
        [PremiseStatus.FOR_SALE]: {
            textId: 'jm.components.PremisesList.available',
            color: TagColor.GREEN,
            type: TagType.PREMISE,
        },
        [PremiseStatus.SOLD]: {
            textId: 'jm.components.PremisesList.sold',
            color: TagColor.RED,
            type: TagType.PREMISE,
        },
        [PremiseStatus.BOOKED]: {
            textId: 'jm.components.PremisesList.booked',
            color: TagColor.ORANGE,
            type: TagType.PREMISE,
        },
        [PremiseStatus.RENTED]: {
            textId: 'jm.components.PremisesList.rented',
            color: TagColor.RED,
            type: TagType.PREMISE,
        },

        // CASES
        [CaseStatus.REGISTERED]: {
            textId: 'jm.features.errands.errandsList.errandStatus.statusRegistered',
            color: TagColor.BLUE,
            type: TagType.CASE,
        },
        [CaseStatus.ONGOING]: {
            textId: 'jm.features.errands.errandsList.errandStatus.statusOngoing',
            color: TagColor.YELLOW,
            type: TagType.CASE,
        },
        [CaseStatus.EXECUTED]: {
            textId: 'jm.features.errands.errandsList.errandStatus.statusExecuted',
            color: TagColor.GREEN,
            type: TagType.CASE,
        },
        [CaseStatus.COMPLETED]: {
            textId: 'jm.features.errands.errandsList.errandStatus.statusCompleted',
            color: TagColor.GRAY,
            type: TagType.CASE,
        },

        // RENTAL
        [RentalStatus.PLANNED]: {
            textId: 'jm.components.ResidenceTags.planned',
            color: TagColor.GRAY,
            type: TagType.RENTAL,
        },
        [RentalStatus.CONSTRUCTION_START]: {
            textId: 'jm.components.ResidenceTags.constructionStart',
            color: TagColor.GRAY,
            type: TagType.RENTAL,
        },
        [RentalStatus.INTERMEDIATION]: {
            textId: 'jm.components.ResidenceTags.intermediation',
            color: TagColor.GREEN,
            type: TagType.RENTAL,
        },
        [RentalStatus.MOVE_IN_READY]: {
            textId: 'jm.components.ResidenceTags.moveInReady',
            color: TagColor.GREEN,
            type: TagType.RENTAL,
        },
        [RentalStatus.COMPLETED]: {
            textId: 'jm.components.ResidenceTags.completed',
            color: TagColor.BLUE,
            type: TagType.RENTAL,
        },
    };

    const getTagValues = (): TagValues => {
        // THEME COLOR
        if (tagColor && tagColorMap[tagColor]) {
            return tagColorMap[tagColor]!;
        }

        // STATUS
        if (status && statusMap[status]) {
            return statusMap[status];
        }

        // FALLBACK
        return {
            textId: '',
            color: tagColor || TagColor.DEFAULT,
            type: undefined,
        };
    };

    const { textId, color, type } = getTagValues();

    const classes = classnames('tag', className, {
        'tag--old': tagDesign === TagDesign.OLD,
        'tag--dot': tagDesign === TagDesign.DOT,
        'tag--filled': tagDesign !== TagDesign.DOT,

        'tag--black': color === TagColor.BLACK && type !== TagType.OBJECT && type !== TagType.PROJECT,
        'tag--blue': color === TagColor.BLUE && type !== TagType.OBJECT && type !== TagType.PROJECT,
        'tag--gray': color === TagColor.GRAY && type !== TagType.OBJECT && type !== TagType.PROJECT,
        'tag--green': color === TagColor.GREEN && type !== TagType.OBJECT && type !== TagType.PROJECT,
        'tag--orange': color === TagColor.ORANGE && type !== TagType.OBJECT && type !== TagType.PROJECT,
        'tag--red': color === TagColor.RED && type !== TagType.OBJECT && type !== TagType.PROJECT,
        'tag--yellow': color === TagColor.YELLOW && type !== TagType.OBJECT && type !== TagType.PROJECT,
        
        'sale-status--on-sale': color === TagColor.GREEN && (type === TagType.OBJECT || type === TagType.PROJECT),
        'sale-status--reserved': color === TagColor.ORANGE && (type === TagType.OBJECT || type === TagType.PROJECT),
        'sale-status--sold': color === TagColor.RED && (type === TagType.OBJECT || type === TagType.PROJECT),
        'sale-status--vip': color === TagColor.YELLOW && (type === TagType.OBJECT || type === TagType.PROJECT),
        'sale-status--upcoming': color === TagColor.BLUE && (type === TagType.OBJECT || type === TagType.PROJECT),
        'sale-status--booked': color === TagColor.GRAY && (type === TagType.OBJECT || type === TagType.PROJECT),
        
        'tag--senior': color === TagColor.SENIOR,
        'tag--premium': color === TagColor.PREMIUM,
        'tag--smarta': color === TagColor.SMARTA,
    });

    if(textId === '' && !tagText) return;
    return (
        <span className={classes} >
            {tagDesign === TagDesign.DOT && <span className='tag__dot' />}
            <span className='tag__text'>
                {textId !== '' 
                    ? <FormattedMessage id={textId} />
                    : tagText
                }
            </span>
        </span>
    )
};
